import language from 'config/language';
import { EntityType } from 'enums';
import { IContact, IUser } from 'interfaces';
import { getValidationText, validateNumber } from 'utils/phone';
import { validate as uuidValidate } from 'uuid';

/**
 * Can be used to validate multiple requirements on a single field
 */
export const composeValidators =
  (
    ...validators: (((value: any, allValues: any) => any) | null | undefined)[]
  ) =>
  (value: any, allValues: any) =>
    validators.reduce((error, validator) => {
      if (!validator) {
        return undefined;
      }
      return error || validator(value, allValues);
    }, undefined);

/** Required Field must have any value other than undefined and only spaces */
export const required = (value: any): string | null => {
  switch (typeof value) {
    case 'boolean':
      if (!value) {
        return language.validation.required;
      }
      break;
    case 'number':
      if (!value && value !== 0) {
        return language.validation.required;
      }
      break;
    case 'string':
      if (value === undefined || value.trim() === '') {
        return language.validation.required;
      }
      break;
    default:
      if (value === undefined || value === null) {
        return language.validation.required;
      }
      break;
  }
  return null;
};

export const requiredArray = (value: any[] | null) => {
  if (!value || value.length === 0) {
    return language.validation.required;
  }
  return null;
};

/** Required Field must be a valid email format */
export const email = (value: any): string | null => {
  // We don't check empty values.  Use required validation for that
  if (!value) {
    return null;
  }

  if (!/^[a-zA-Z0-9.'_%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,63}$/gm.test(value)) {
    return language.validation.email;
  }

  if (value.includes('gmail') || value.includes('hotmail')) {
    return 'Email must be from a work email account';
  }

  return null;
};

/** Required Field must be a valid email format */
export const emailDomain = (value: any): string | null => {
  // We don't check empty values.  Use required validation for that
  if (!value) {
    return null;
  }

  if (!/^[@a-zA-Z0-9.-]+\.[a-zA-Z]{2,63}$/gm.test(value)) {
    return language.validation.emailDomain;
  }

  return null;
};

/** Required Field must be a valid phone format */
export const phone = (value: any): string | null => {
  // We don't check empty values.  Use required validation for that
  if (!value) {
    return null;
  }

  const invalid = validateNumber(value);
  if (invalid) {
    return getValidationText(invalid);
  }
  return null;
};

/** Checks if there is at least one value for phone or email fields */
export const identifier = (value: any, allValues: any): string | null => {
  if (value === '') {
    return null;
  }
  const emails = allValues.emails.filter((e: string) => e && e !== '');
  const phones = allValues.phone.filter((p: string) => p && p !== '');
  if (emails.length === 0 && phones.length === 0) {
    return 'A contact must have at least a phone or email to be considered a valid contact.';
  }

  return null;
};

export const atLeastOnePhoneOrEmail = (index: number) => {
  return index === 0
    ? identifier
    : (value: any, allValues: any): string | null => null;
};

// Validate that the attendees field contains at least 1 User and 1 Contact
export const attendees = (values: Array<IContact | IUser>): string | null => {
  if (!values) {
    return language.validation.attendees;
  }

  const users = values.some((entity) => entity.entityType === EntityType.USER);
  const contacts = values.some(
    (entity) => entity.entityType === EntityType.CONTACT,
  );

  if (!(users && contacts)) {
    return language.validation.attendees;
  }
  return null;
};

const urlTest =
  /^((http(s?)):)?\/\/(((([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!$&'()*+,;=]|:)*@)?(((\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5])\.(\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5])\.(\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5])\.(\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5]))|((([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.)+(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.?)(:\d*)?)(\/((([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!$&'()*+,;=]|:|@)+(\/(([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!$&'()*+,;=]|:|@)*)*)?)?(\?((([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!$&'()*+,;=]|:|@)|[\uE000-\uF8FF]|\/|\?)*)?(#((([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!$&'()*+,;=]|:|@)|\/|\?)*)?$/i;

/** Required Field must be a valid url format */
export const url = (value: any): string | null => {
  // We don't check empty values.  Use required validation for that
  if (!value || value === 'http://' || value === 'https://') {
    return null;
  }

  if (!urlTest.test(value)) {
    return 'Please enter a valid website url.';
  }

  return null;
};

export const httpsUrl = (value: any): string | null => {
  if (!value) {
    return null;
  }
  if (!value.startsWith('https://')) {
    return 'Please ensure your url starts with https://';
  }

  if (!urlTest.test(value)) {
    return 'Please enter a valid url.';
  }

  return null;
};

export const endTimeIsLater = (
  value: any,
  allValues: Record<string, any>,
): null | string => {
  if (allValues.endTime - allValues.startTime <= 0) {
    return 'The end time must be later than the start time.';
  }
  return null;
};

export const uuid = (value: string): string | null => {
  const isUuid = uuidValidate(value);
  if (!isUuid) {
    return 'Please check you have the correct tenant ID, the following format is required: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx';
  }
  return null;
};

export const percentage = (value: number) => {
  if (value <= 0 || value > 100) {
    return 'Please enter a number between 0 and 100';
  }
  return null;
};
